import { subscribe } from 'valtio'

import {
  floorInitState,
  floorState,
  getFeedsInFloor,
} from '../../firebase/hooks/useFloor'
import {
  getSortedHumanParticipants,
  roomState,
  setVideoTrackQuality,
  type TrackPriority,
  type VideoTrackKey,
} from '../webRTCRoom'
import { panelState } from '../../firebase/hooks/usePanel'
import { trayState } from '../../firebase/hooks/useTray'
import { mixesState } from '../../firebase/hooks/useMixes'
import { type Feed, type Mix } from '../../firebase/model/mix'
import { type DeepReadonly } from '../../firebase/typeUtils'
import { COVER_ZOOM, getStandardLayoutFeed } from '../../Stage/layouts'
import { getStandardLayouts } from '../../Designs/blocs/useRoomDesign'

// A feed at or above this demand gets 'high'; anything else in the floor gets
// 'standard'. For reference: a 50/50 side-by-side split is ~0.71, a quarter
// of the floor is 0.5, a 3x2 grid slot is ~0.41.
const HIGH_DEMAND_THRESHOLD = 0.45

// How much the source is magnified inside its box. Mirrors the floor's zoom
// geometry (see TrackPositioner): zoom <= COVER_ZOOM fits/covers the box
// (no magnification beyond the box size), zoom above it scales the source up
// to 4x, meaning only a quarter of the source's width is stretched across the
// same box.
const zoomMagnification = (zoom: number) =>
  zoom <= COVER_ZOOM ? 1 : 1 + ((zoom - COVER_ZOOM) / (100 - COVER_ZOOM)) * 3

// Resolution demand of a feed: the fraction of the floor's linear resolution
// its box occupies (sqrt of the area fraction), scaled by zoom magnification.
// A small pip demands little; the same pip zoomed 4x demands as much as a box
// four times its size.
const feedResolutionDemand = (feed: DeepReadonly<Feed>) => {
  const coverage = Math.sqrt((feed.position.w / 100) * (feed.position.h / 100))
  return coverage * zoomMagnification(feed.crop.zoom)
}

const demandToQuality = (demand: number): TrackPriority =>
  demand >= HIGH_DEMAND_THRESHOLD ? 'high' : 'standard'

// Quality for a participant's track in a custom mix. When the same source is
// placed in several slots, the most demanding one wins.
const customMixTrackQuality = (
  participantId: number,
  key: VideoTrackKey,
  mix: DeepReadonly<Mix> | undefined
): TrackPriority => {
  if (!mix) return 'low'

  const demands = mix.feeds
    .filter(
      (f) =>
        f.media &&
        f.media.id === participantId &&
        (f.media.type === key ||
          (participantId === 0 && f.media.type === 'tray' && key === 'screen'))
    )
    .map(feedResolutionDemand)

  if (demands.length === 0) return 'low'
  return demandToQuality(Math.max(...demands))
}

const wantedTrackQuality = (
  participantId: number,
  key: VideoTrackKey
): TrackPriority => {
  const { floor } = floorState
  if (!floor) return 'low'

  if (floor.layout === 'custom') {
    const mix = mixesState.mixes.find((m) => m.id === floor.mixId)
    return customMixTrackQuality(participantId, key, mix)
  }

  const feedsInFloor = getFeedsInFloor(
    floor,
    getSortedHumanParticipants(),
    panelState,
    mixesState.mixes
  )

  if (
    !feedsInFloor.some(
      (f) =>
        f.id === participantId &&
        (f.type === key ||
          (participantId === 0 && key === 'screen' && f.type === 'tray'))
    )
  ) {
    return 'low'
  }

  // Resolve the same box geometry the floor renders this feed with
  const feed = getStandardLayoutFeed(
    floor.layout,
    getStandardLayouts(),
    feedsInFloor,
    key,
    participantId
  )

  // The design (and with it the layout geometry) may not have resolved yet —
  // fall back to a safe middle ground for feeds we know are in the floor.
  if (!feed) return 'standard'

  return demandToQuality(feedResolutionDemand(feed))
}

const VIDEO_TRACK_KEYS: VideoTrackKey[] = ['camera', 'screen', 'mobile']

const syncRemoteTracksQuality = () => {
  const remoteParticipants = roomState.participants.filter((p) => !p.isLocal)

  // wait for the floor to be initialized
  if (!floorInitState.isFloorReady || !floorState.floor) return

  remoteParticipants.forEach((p) => {
    VIDEO_TRACK_KEYS.forEach((key) => {
      void setVideoTrackQuality(p.id, key, wantedTrackQuality(p.id, key))
    })
  })
}

/// ////////////////////////////// ///
/// initialization and destruction ///
/// ////////////////////////////// ///

const subscriptions: (() => void)[] = []

const init = () => {
  subscriptions.push(subscribe(roomState.participants, syncRemoteTracksQuality))
  subscriptions.push(subscribe(floorState, syncRemoteTracksQuality))
  subscriptions.push(subscribe(panelState, syncRemoteTracksQuality))
  subscriptions.push(subscribe(trayState, syncRemoteTracksQuality))
  subscriptions.push(subscribe(mixesState, syncRemoteTracksQuality))
  syncRemoteTracksQuality()
}

const destroy = () => {
  while (subscriptions.length > 0) {
    subscriptions.shift()?.()
  }
}

export { syncRemoteTracksQuality, init, destroy }
